import { useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

export const FIT_OPTIONS = [
  { value: 'tight', label: 'Tight', description: 'Form-hugging fit' },
  { value: 'regular', label: 'Regular', description: 'Standard comfortable fit' },
  { value: 'loose', label: 'Loose', description: 'Relaxed fit' },
  { value: 'oversized', label: 'Oversized', description: 'Extra roomy fit' },
];

/**
 * Compress an image to reduce upload size.
 */
function compressImage(dataUrl, maxWidth = 1200, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
      const base64Data = compressedDataUrl.split(',')[1];
      resolve({ base64Data, fullDataUrl: compressedDataUrl });
    };
    img.src = dataUrl;
  });
}

/**
 * Download a generated image (URL or base64).
 */
export function downloadTryOnImage(imageData) {
  if (!imageData) return;
  try {
    if (imageData.startsWith('http')) {
      fetch(imageData)
        .then((r) => r.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'virtualfit-tryon.png';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        });
      return;
    }
    const link = document.createElement('a');
    link.href = imageData.startsWith('data:') ? imageData : `data:image/png;base64,${imageData}`;
    link.download = 'virtualfit-tryon.png';
    link.click();
  } catch {
    window.open(imageData, '_blank');
  }
}

/**
 * Share to social media.
 */
export function shareTryOn(platform) {
  const shareText = 'Check out my virtual try-on from VirtualFit! Try clothes before you buy with AI.';
  const shareUrl = 'https://virtualfit.work';
  const urls = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
    instagram: `https://www.instagram.com/`,
  };
  window.open(urls[platform], '_blank', 'width=600,height=400');
}

/**
 * useVirtualTryOn - shared hook for AI virtual try-on generation.
 *
 * @param {Object} opts
 * @param {'authenticated'|'free-trial'} opts.mode
 * @param {string} [opts.token]        - JWT token (authenticated mode)
 * @param {string} [opts.fingerprint]  - Browser fingerprint (free-trial mode)
 * @param {Function} [opts.onComplete] - Callback after successful generation, receives result data
 * @param {Function} [opts.onTrialExceeded] - Callback when free-trial limit reached
 */
export default function useVirtualTryOn({ mode, token, fingerprint, onComplete, onTrialExceeded } = {}) {
  const [userPhoto, setUserPhoto] = useState(null);
  const [userPhotoPreview, setUserPhotoPreview] = useState(null);
  const [clothesImage, setClothesImage] = useState(null);
  const [clothesPreview, setClothesPreview] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [size, setSize] = useState('M');
  const [fitPreference, setFitPreference] = useState('regular');

  const isFreeTrial = mode === 'free-trial';
  const generateEndpoint = isFreeTrial ? '/api/free-trial/generate-async' : '/api/tryon/generate-async';
  const pollEndpoint = isFreeTrial ? '/api/free-trial/job' : '/api/tryon/job';

  const handleImageUpload = useCallback((e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, JPEG, WebP).');
      return;
    }

    setError('');

    const reader = new FileReader();
    reader.onloadend = async () => {
      const fullDataUrl = reader.result;
      try {
        const { base64Data, fullDataUrl: compressedUrl } = await compressImage(fullDataUrl);
        if (type === 'user') {
          setUserPhoto(base64Data);
          setUserPhotoPreview(compressedUrl);
        } else {
          setClothesImage(base64Data);
          setClothesPreview(compressedUrl);
        }
      } catch {
        const base64Data = fullDataUrl.split(',')[1];
        if (type === 'user') {
          setUserPhoto(base64Data);
          setUserPhotoPreview(fullDataUrl);
        } else {
          setClothesImage(base64Data);
          setClothesPreview(fullDataUrl);
        }
      }
    };
    reader.onerror = () => setError('Failed to read file. Please try again.');
    reader.readAsDataURL(file);
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!userPhoto || !clothesImage) {
      setError('Please upload both your photo and a clothing image');
      return;
    }

    setGenerating(true);
    setError('');
    setGeneratedImage(null);
    setProgress('Starting image generation...');

    // --- Start async job ---
    const startJob = () =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}${generateEndpoint}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        if (!isFreeTrial && token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.timeout = 30000;

        xhr.onload = function () {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(data);
            } else {
              if (isFreeTrial && data.detail?.error === 'free_trial_exceeded') {
                onTrialExceeded?.();
              }
              reject(new Error(typeof data.detail === 'string' ? data.detail : data.detail?.message || 'Failed to start generation'));
            }
          } catch {
            reject(new Error('Failed to process response'));
          }
        };
        xhr.onerror = () => reject(new Error('Connection error. Please check your internet and try again.'));
        xhr.ontimeout = () => reject(new Error('Upload timed out. Try using smaller images.'));

        const body = {
          user_photo_base64: userPhoto,
          clothes_image_base64: clothesImage,
          size,
          fit_preference: fitPreference,
        };
        if (isFreeTrial && fingerprint) {
          body.browser_fingerprint = fingerprint;
        }
        xhr.send(JSON.stringify(body));
      });

    // --- Poll for result ---
    const pollJob = (jobId) =>
      new Promise((resolve, reject) => {
        const maxAttempts = 90;
        let attempts = 0;

        const poll = () => {
          const pct = Math.min(95, 10 + Math.round((attempts / 45) * 85));
          setProgress(`Creating your look... ${pct}%`);

          const xhr = new XMLHttpRequest();
          let url = `${API_URL}${pollEndpoint}/${jobId}`;
          if (isFreeTrial && fingerprint) {
            url += `?fingerprint=${encodeURIComponent(fingerprint)}`;
          }
          xhr.open('GET', url, true);
          if (!isFreeTrial && token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }
          xhr.timeout = 15000;

          xhr.onload = function () {
            try {
              const data = JSON.parse(xhr.responseText);
              if (data.status === 'completed') {
                setProgress('Done!');
                resolve(data);
              } else if (data.status === 'failed') {
                reject(new Error(data.error || 'Generation failed. Please try with different images.'));
              } else if (data.status === 'pending' || data.status === 'processing') {
                attempts++;
                if (attempts >= maxAttempts) reject(new Error('Generation timed out. Please try again.'));
                else setTimeout(poll, 2000);
              } else {
                reject(new Error('Unknown job status'));
              }
            } catch {
              reject(new Error('Failed to check status'));
            }
          };
          xhr.onerror = function () {
            attempts++;
            if (attempts >= maxAttempts) reject(new Error('Connection error during status check'));
            else setTimeout(poll, 2000);
          };
          xhr.ontimeout = function () {
            attempts++;
            if (attempts >= maxAttempts) reject(new Error('Request timed out. Please check your connection.'));
            else setTimeout(poll, 2000);
          };
          xhr.send();
        };

        poll();
      });

    try {
      setProgress('Uploading images...');
      const jobResult = await startJob();
      if (!jobResult.job_id) throw new Error('No job ID received from server');

      setProgress('AI is creating your look... 5%');
      const result = await pollJob(jobResult.job_id);

      const imageData = result.generated_image_base64 || result.cloudinary_url || result.result;
      if (!imageData) throw new Error('Image generation completed but no image was returned. Please try again.');

      setGeneratedImage(imageData);
      onComplete?.(result);
    } catch (err) {
      setError(err.message || 'Generation failed. Please try again.');
    } finally {
      setGenerating(false);
      setProgress('');
    }
  }, [userPhoto, clothesImage, size, fitPreference, token, fingerprint, isFreeTrial, generateEndpoint, pollEndpoint, onComplete, onTrialExceeded]);

  const clearUserPhoto = useCallback(() => {
    setUserPhoto(null);
    setUserPhotoPreview(null);
  }, []);

  const clearClothesImage = useCallback(() => {
    setClothesImage(null);
    setClothesPreview(null);
  }, []);

  const reset = useCallback(() => {
    clearUserPhoto();
    clearClothesImage();
    setGeneratedImage(null);
    setError('');
  }, [clearUserPhoto, clearClothesImage]);

  return {
    // State
    userPhoto,
    userPhotoPreview,
    clothesImage,
    clothesPreview,
    generatedImage,
    generating,
    progress,
    error,
    size,
    fitPreference,
    // Setters
    setSize,
    setFitPreference,
    setError,
    setGeneratedImage,
    // Actions
    handleImageUpload,
    handleGenerate,
    handleDownload: () => downloadTryOnImage(generatedImage),
    handleShare: shareTryOn,
    clearUserPhoto,
    clearClothesImage,
    reset,
  };
}
